import { z } from 'zod';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
const name = z.string().trim().min(1).max(120);
const uuid = z.string().uuid();
const email = z.string().trim().toLowerCase().email().max(254);
const documentType = z.string().trim().min(1).max(8);
const documentNumber = z.string().trim().min(3).max(32);
const phone = z.string().trim().min(6).max(32);
const shortText = z.string().trim().max(300);

/**
 * A student the school creates (scoping 3.5). Email is optional: minors without one get their
 * account from a guardian later (#63); the enrollment may come in the same call.
 */
export const createStudentInputSchema = z.object({
  givenName: name,
  familyName: name,
  email: email.optional(),
  documentType,
  documentNumber: documentNumber.optional(),
  birthDate: isoDate.optional(),
  sedeId: uuid.optional(),
  enrollment: z
    .object({ schoolYearId: uuid, groupId: uuid, enrolledOn: isoDate.optional() })
    .optional(),
});

/**
 * Editable student fields; `photoKey` is an upload key under the tenant's `photo` prefix.
 */
export const updateStudentInputSchema = z
  .object({
    givenName: name,
    familyName: name,
    email: email.nullable(),
    documentType,
    documentNumber: documentNumber.nullable(),
    birthDate: isoDate.nullable(),
    sedeId: uuid.nullable(),
    photoKey: z.string().trim().max(500).nullable(),
  })
  .partial();

/**
 * Places a student in a group for a school year (one enrollment per year).
 */
export const enrollStudentInputSchema = z.object({
  schoolYearId: uuid,
  groupId: uuid,
  enrolledOn: isoDate.optional(),
});

/**
 * Moves a student to another group in the same year, or closes the enrollment.
 */
export const updateEnrollmentInputSchema = z
  .object({
    groupId: uuid,
    status: z.enum(['active', 'withdrawn', 'transferred', 'completed']),
  })
  .partial();

/**
 * Lifecycle states of a student in the school.
 */
export const studentStatusSchema = z.enum(['active', 'withdrawn', 'graduated']);

/**
 * A dated status transition (withdrawal, graduation, reactivation).
 */
export const changeStudentStatusInputSchema = z.object({
  status: studentStatusSchema,
  effectiveOn: isoDate,
  reason: shortText.optional(),
});

/**
 * Sensitive fields (scoping 3.5, 3.14): stored apart, returned only with `students:sensitive`.
 */
export const studentSensitiveDataInputSchema = z
  .object({
    eps: z.string().trim().max(120).nullable(),
    allergies: shortText.nullable(),
    disabilities: shortText.nullable(),
    religion: z.string().trim().max(80).nullable(),
    ethnicity: z.string().trim().max(80).nullable(),
  })
  .partial();

/**
 * A guardian (acudiente) the school creates; linked to students separately.
 */
export const createGuardianInputSchema = z.object({
  givenName: name,
  familyName: name,
  email: email.optional(),
  phone: phone.optional(),
  documentType: documentType.optional(),
  documentNumber: documentNumber.optional(),
});
export const updateGuardianInputSchema = z
  .object({
    givenName: name,
    familyName: name,
    email: email.nullable(),
    phone: phone.nullable(),
    documentType: documentType.nullable(),
    documentNumber: documentNumber.nullable(),
  })
  .partial();

/**
 * Links a guardian to a student with the relationship and the two flags; at most one primary
 * contact per student.
 */
export const linkGuardianInputSchema = z.object({
  guardianUserId: uuid,
  relationship: z.string().trim().min(1).max(40).default('acudiente'),
  isPrimary: z.boolean().default(false),
  billingResponsible: z.boolean().default(false),
});
export const updateGuardianLinkInputSchema = linkGuardianInputSchema
  .omit({ guardianUserId: true })
  .partial();

/**
 * A teacher the school creates; the invitation to sign in is a separate step (#63).
 */
export const createTeacherInputSchema = z.object({
  givenName: name,
  familyName: name,
  email,
  phone: phone.optional(),
  documentType: documentType.optional(),
  documentNumber: documentNumber.optional(),
});
export const updateTeacherInputSchema = z
  .object({
    givenName: name,
    familyName: name,
    email,
    phone: phone.nullable(),
    documentType: documentType.nullable(),
    documentNumber: documentNumber.nullable(),
  })
  .partial();

/**
 * One assignment: a group and a subject, or the homeroom (director de grupo) with no subject.
 */
export const teacherAssignmentInputSchema = z.object({
  groupId: uuid,
  subjectId: uuid.nullable(),
  isHomeroom: z.boolean().default(false),
});

/**
 * Replaces a teacher's assignments for one school year.
 */
export const putTeacherAssignmentsInputSchema = z.object({
  schoolYearId: uuid,
  assignments: z.array(teacherAssignmentInputSchema).max(200),
});

/**
 * Query of the guardians list.
 */
export const guardiansQuerySchema = z.object({
  search: z.string().trim().max(80).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});

/**
 * Body of `POST /students`.
 */
export type CreateStudentInput = z.input<typeof createStudentInputSchema>;
/**
 * Body of `PATCH /students/{id}`.
 */
export type UpdateStudentInput = z.input<typeof updateStudentInputSchema>;
/**
 * Body of `POST /students/{id}/enrollments`.
 */
export type EnrollStudentInput = z.input<typeof enrollStudentInputSchema>;
/**
 * Body of `PATCH /students/{id}/enrollments/{enrollmentId}`.
 */
export type UpdateEnrollmentInput = z.input<typeof updateEnrollmentInputSchema>;
/**
 * A student's lifecycle status.
 */
export type StudentStatus = z.infer<typeof studentStatusSchema>;
/**
 * Body of `POST /students/{id}/status`.
 */
export type ChangeStudentStatusInput = z.input<typeof changeStudentStatusInputSchema>;
/**
 * Body of `PUT /students/{id}/sensitive`.
 */
export type StudentSensitiveDataInput = z.input<typeof studentSensitiveDataInputSchema>;
/**
 * Body of `POST /guardians`.
 */
export type CreateGuardianInput = z.input<typeof createGuardianInputSchema>;
/**
 * Body of `PATCH /guardians/{id}`.
 */
export type UpdateGuardianInput = z.input<typeof updateGuardianInputSchema>;
/**
 * Body of `POST /students/{id}/guardians`.
 */
export type LinkGuardianInput = z.input<typeof linkGuardianInputSchema>;
/**
 * Body of `PATCH /students/{id}/guardians/{guardianId}`.
 */
export type UpdateGuardianLinkInput = z.input<typeof updateGuardianLinkInputSchema>;
/**
 * Body of `POST /teachers`.
 */
export type CreateTeacherInput = z.input<typeof createTeacherInputSchema>;
/**
 * Body of `PATCH /teachers/{id}`.
 */
export type UpdateTeacherInput = z.input<typeof updateTeacherInputSchema>;
/**
 * Body of `PUT /teachers/{id}/assignments`.
 */
export type PutTeacherAssignmentsInput = z.input<typeof putTeacherAssignmentsInputSchema>;

/**
 * What the write endpoints answer: the person's user id.
 */
export type PersonRef = { id: string };

/**
 * A student's history: every enrollment and every status transition.
 */
export type StudentHistory = {
  enrollments: {
    id: string;
    schoolYearId: string;
    schoolYearName: string;
    groupId: string;
    groupName: string;
    status: string;
    enrolledOn: string | null;
  }[];
  statusEvents: { status: string; effectiveOn: string; reason: string | null; createdAt: string }[];
};

/**
 * The sensitive fields of one student, as `GET /students/{id}/sensitive` returns them.
 */
export type StudentSensitiveData = {
  eps: string | null;
  allergies: string | null;
  disabilities: string | null;
  religion: string | null;
  ethnicity: string | null;
};

/**
 * A guardian as the list shows them, with how many students they are linked to.
 */
export type GuardianListItem = {
  id: string;
  givenName: string | null;
  familyName: string | null;
  email: string | null;
  phone: string | null;
  documentType: string | null;
  documentNumber: string | null;
  studentsCount: number;
};
